import {
  CuratedMedicineDetails,
  CuratedMedicinesByCategoryIdResponse,
  CuratedMedicinesSearchResult,
  CurateMedicineRequest,
  CurateMedicineResponse,
  DeleteMedicinesRequest,
  DeleteMedicinesResponse,
  MedicineCategory,
  MedicineCategoryCreate,
  MedicineCategoryUpdate,
  RemoveMedicinesFromCategoryRequest,
  SearchMedicineByTermResponse,
  UpdateMedicineCurationDetailsRequest,
  UpdateMedicineCurationDetailsResponse,
} from '@/lib/apis/types'
import {
  DeleteMedicineByCategoryIdUrl,
  GetMedicineByAliasUrl,
  GetMedicineDetailsByCategoryIdUrl,
  UpdateMedicineCategoryUrl,
} from '@/lib/apis/routes'
import { CacheKeys, MutationKeys, QueryKeys } from '@/lib/apis/service'
import WebRequest from '@/lib/apis/WebRequest'
import { ApiClient } from '@/lib/apis/clients/ApiClient'
import { useMutation, useQuery } from '@tanstack/react-query'
import { MedicineCacheRevalidator } from '@/app/actions/prescriptions'

export namespace GetAllMedicineCategories {
  export function api(client: ApiClient): Promise<Array<MedicineCategory>> {
    const req = WebRequest.GET('/prescriptions/category').cached(
      CacheKeys.PrescriptionCategories(),
    )

    return client.execute(req)
  }
}

export namespace GetAllMedicineByCategoryAlias {
  type Params = {
    alias: string
  }

  export function api({ alias }: Params): WebRequest {
    return WebRequest.GET(`/prescriptions/category/by-alias/${alias}`).cached(
      CacheKeys.PrescriptionCategoryByAlias(alias),
    )
  }
}

export namespace SearchMedicine {
  type Params = {
    word: string
    category?: string
  }

  export function api({ word, category }: Params): WebRequest {
    const categoryParam = category ? `&category=${category}` : ''
    return WebRequest.GET(
      `/prescriptions/medicine/concise?word=${word}${categoryParam}`,
    )
  }

  export function query(client: ApiClient, { word, category }: Params) {
    return {
      queryFn: () =>
        client.execute<SearchMedicineByTermResponse>(api({ word, category })),
      queryKey: QueryKeys.SearchMedicine(word, category),
      enabled: word.length >= 3,
      staleTime: 5 * 60 * 1000,
    }
  }
}

export namespace GetMedicineByAlias {
  type Params = {
    categoryAlias: string
    alias: string
  }

  export function api({ categoryAlias, alias }: Params): WebRequest {
    return WebRequest.GET(GetMedicineByAliasUrl(alias)).cached(
      CacheKeys.PrescriptionMedicine(categoryAlias, alias),
    )
  }
}

export namespace GetAllMedicineCategoryDetails {
  export function api(): WebRequest {
    return WebRequest.GET('/admin/medicine/categories').cached(
      CacheKeys.PrescriptionCategoryDetails(),
    )
  }
}

export namespace UpdateMedicineCategoryDetails {
  type Params = {
    id: string
    data: MedicineCategoryUpdate
  }

  export function api({ id, data }: Params): WebRequest {
    return WebRequest.POST(UpdateMedicineCategoryUrl(id)).body(data)
  }

  export function mutation(client: ApiClient, id: string) {
    return {
      mutationFn: async (params: Omit<Params, 'id'>) =>
        await client.executeEmpty(api({ id, ...params })),
      mutationKey: MutationKeys.UpdatePrescriptionCategory(id),
    }
  }
}

export namespace GetCuratedMedicinesByCategoryId {
  export async function api(
    client: ApiClient,
    categoryId: string,
  ): Promise<CuratedMedicinesByCategoryIdResponse> {
    const req = WebRequest.GET(GetMedicineDetailsByCategoryIdUrl(categoryId))
    return client.execute(req)
  }
}

export namespace RemoveMedicinesFromCategory {
  export function useMutationApi(client: ApiClient, categoryId: string) {
    return useMutation({
      mutationFn: async (body: RemoveMedicinesFromCategoryRequest) =>
        client.executeEmpty(
          WebRequest.POST(
            `/admin/medicine/categories/${categoryId}/remove-medicines`,
          ).body(body),
        ),
      mutationKey: MutationKeys.RemoveMedicinesFromCategory(categoryId),
    })
  }
}

export namespace CreateMedicineCategory {
  type Params = {
    data: MedicineCategoryCreate
  }

  export function api({ data }: Params): WebRequest {
    return WebRequest.POST('/admin/medicine/categories').body(data)
  }

  export function mutation(client: ApiClient) {
    return {
      mutationFn: async (params: Params) =>
        await client.executeEmpty(api(params)),
      mutationKey: MutationKeys.CreateMedicineCategory(),
    }
  }
}

export namespace DeleteMedicineCategoryById {
  type Params = {
    id: string
  }

  export function api({ id }: Params): WebRequest {
    return WebRequest.DELETE(DeleteMedicineByCategoryIdUrl(id))
  }

  export function mutation(client: ApiClient, id: string) {
    return {
      mutationFn: async (params: Omit<Params, 'id'>) =>
        await client.executeEmpty(api({ id, ...params })),
      mutationKey: MutationKeys.DeleteMedicineCategoryById(id),
    }
  }
}

export namespace CurateMedicine {
  // Need to purge category list too, as this can change the category with count == 0 into 1, and
  // the category list should start showing that category
  export function useCurateMedicineMutation(
    client: ApiClient,
    cacheRevalidator: MedicineCacheRevalidator,
  ) {
    return useMutation({
      mutationFn: async (body: CurateMedicineRequest) => {
        const req = WebRequest.POST('/admin/medicine/curate').body(body)
        const res = await client.execute<CurateMedicineResponse>(req)

        await cacheRevalidator({
          categoryAliases: res.categoryAliases,
          categoryMedicineAliasPairs: res.categoryAliases.map(
            (categoryAlias) => ({
              medicineAlias: res.alias,
              categoryAlias,
            }),
          ),
        })
      },
      mutationKey: MutationKeys.CurateMedicines(),
    })
  }
}

export namespace UpdateMedicineCuration {
  // Need to purge category list too, as this can change the category with count > 0 into 0, and
  // the category list should stop showing that category
  export function useUpdateMutation(
    client: ApiClient,
    medicineId: string,
    cacheRevalidator: MedicineCacheRevalidator,
  ) {
    return useMutation({
      mutationFn: async (body: UpdateMedicineCurationDetailsRequest) => {
        const req = WebRequest.POST(
          `/admin/medicine/medicines/${medicineId}/curation`,
        ).body(body)
        const res =
          await client.execute<UpdateMedicineCurationDetailsResponse>(req)

        await cacheRevalidator({
          categoryAliases: res.categoryAliases,
          categoryMedicineAliasPairs: res.categoryAliases.map(
            (categoryAlias) => ({
              medicineAlias: res.alias,
              categoryAlias,
            }),
          ),
        })
      },
      mutationKey: MutationKeys.UpdateCuratedMedicine(medicineId),
    })
  }
}

export namespace GetCuratedMedicineDetailsById {
  export function useCuratedMedicineQuery(
    client: ApiClient,
    medicineId: string,
  ) {
    return useQuery({
      queryFn: async () => {
        const req = WebRequest.GET(`/admin/medicine/medicines/${medicineId}`)
        return client.execute<CuratedMedicineDetails>(req)
      },
      queryKey: QueryKeys.GetCuratedMedicineDetails(medicineId),
    })
  }
}

export namespace DeleteMedicines {
  export function useDeleteMutation(
    client: ApiClient,
    cacheRevalidator: MedicineCacheRevalidator,
  ) {
    return useMutation({
      mutationFn: async (medicineIds: Array<string>) => {
        const body: DeleteMedicinesRequest = { medicineIds }
        const req = WebRequest.POST('/admin/medicine/medicines/delete').body(
          body,
        )
        const res = await client.execute<DeleteMedicinesResponse>(req)
        await cacheRevalidator({
          categoryAliases: res.categoryAliases,
          categoryMedicineAliasPairs: res.aliases.flatMap((medicineAlias) =>
            res.categoryAliases.map((categoryAlias) => ({
              categoryAlias,
              medicineAlias,
            })),
          ),
        })
      },
    })
  }
}

export namespace FindMedicineCurationsByVirtualProductId {
  export function api(
    client: ApiClient,
    vpId: string,
  ): Promise<CuratedMedicinesSearchResult> {
    const req = WebRequest.GET(
      `/admin/medicine/medicines/curations/by-vp-id?vpId=${vpId}`,
    )
    return client.execute(req)
  }

  export function query(client: ApiClient, productId: string | null) {
    return {
      queryKey: QueryKeys.FindMedicineCurationsByVirtualProductId(productId),
      queryFn: () => api(client, productId!),
      enabled: productId !== null,
    }
  }
}
